import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { mdiAccount, mdiAccountMultiple, mdiPlusCircleOutline } from '@mdi/js';
import { TranslateService } from '@ngx-translate/core';
import * as React from 'react';
import { connect } from 'react-redux';

import { CmpsDatePipe } from '../common/date.pipe';
import { QuickSwitchItem } from '../insight/config.insight.model';
import { PopUpFrameInsight } from '../insight/pop-up-frame.insight.component';
import { PopUp } from '../insight/pop-up.component';
import { CacheDateItem } from '../performance-data/cache-date.pd.model';
import iocTypes from '../rvirus.imap';
import { lazyInject } from '../rvirus.ioc';
import { PeriodType } from '../widget/date-range.widget.model';

import { TranslationKeyMap } from './human-insight.i18n';
import { storeStateMapper } from './human-insight.state';
import {
    SuperGroupExtra,
    SuperGroupHumanInsightProps,
    SuperGroupHumanInsightState,
} from './super-group.hi.model';

interface DateRange {
    key: number;
    start: Date;
    end: Date;
    items: QuickSwitchItem[];
}

function deDup(items: QuickSwitchItem[]): QuickSwitchItem[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    return (
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        items &&
        items
            .reduce<QuickSwitchItem[]>((p, c) => {
                if (
                    p.findIndex(
                        (e) => e.initialCriteria === c.initialCriteria,
                    ) === -1
                ) {
                    p.push(c);
                }
                return p;
            }, [])
            .filter((e) => !!e)
    );
}

class SuperGroupHumanInsightImpl extends React.Component<
    SuperGroupHumanInsightProps,
    SuperGroupHumanInsightState
> {
    @lazyInject(iocTypes.TranslateService.identifier)
    private readonly _translateService: TranslateService;

    @lazyInject(iocTypes.CmpsDatePipe.identifier)
    private readonly _datePipe: CmpsDatePipe;

    private readonly _popUpRef: React.RefObject<PopUp>;

    constructor(props: SuperGroupHumanInsightProps) {
        super(props);

        this._propsToState();
        this._onSelectionChange = this._onSelectionChange.bind(this);

        this._popUpRef = React.createRef();
    }

    private _propsToState(): void {
        this.state = {};

        if (this.props.cacheDates && this.props.eventDate) {
            const dateRange = this.props.cacheDates.find(
                (c) =>
                    c.eventDate &&
                    c.eventDate.getTime() === this.props.eventDate.getTime(),
            );
            this.state = { ...this.state, dateRange };
        }
    }

    private _filterRecent(): Array<QuickSwitchItem<SuperGroupExtra>> {
        const items = this.props.filteredItems || [];
        const nonTeam = items
            .filter((e) => !e.extra.teamSuperGroup)
            .reduce((p, c) => {
                return !p ||
                    (c.dateRanges &&
                        c.dateRanges.length !== 0 &&
                        c.dateRanges.some((e) => {
                            return (
                                p.dateRanges &&
                                p.dateRanges.length !== 0 &&
                                e.end >=
                                    p.dateRanges.reduce(
                                        (pp, cc) =>
                                            !pp || cc.end > pp.end ? cc : pp,
                                        null,
                                    ).end
                            );
                        }))
                    ? c
                    : p;
            }, null);
        const team = items.filter((e) => e.extra.teamSuperGroup); // dates won't be shown
        return !nonTeam ? deDup(team) : deDup([nonTeam, ...team]);
    }

    private _filterPrevious(recent: QuickSwitchItem[]): QuickSwitchItem[] {
        const recentDateRanges = recent.reduce((p, c) => {
            if (c.dateRanges) {
                p.push(...c.dateRanges);
            }
            return p;
        }, []);
        const items = (this.props.filteredItems || [])
            .map((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                const dateRanges = e.dateRanges.filter(
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (ee) => recentDateRanges.indexOf(ee) === -1,
                );
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return { ...e, dateRanges } as QuickSwitchItem<SuperGroupExtra>;
            })
            .filter((e) => e.dateRanges && e.dateRanges.length !== 0)
            .sort((a) => (a.extra.teamSuperGroup ? 0 : 1));
        return deDup(items);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _onSelectionChange(item: QuickSwitchItem<SuperGroupExtra>) {
        const orig =
            item &&
            this.props.filteredItems.find(
                (e) => e.initialCriteria === item.initialCriteria,
            );
        if (!orig || this.props.selected === orig) return;
        if (this.props.onSelectionChange) {
            this.props.onSelectionChange({ selected: orig });
        }
    }

    private _getPopUpContent(
        items: Array<QuickSwitchItem<SuperGroupExtra>>,
    ): JSX.Element {
        const previousSuperGroupColumn = (
            dates: DateRange[],
        ): JSX.Element[] => {
            return dates.map((date, i) => (
                <div
                    key={i}
                    className="list"
                >
                    <div className="dateTitle">
                        <span>
                            {this._datePipe.transform(date.start, 'MM-dd-y')}
                            {this.state.dateRange.periodType !==
                                PeriodType.DAY && (
                                <>
                                    {' '}
                                    -{' '}
                                    {this._datePipe.transform(
                                        date.end > this.state.dateRange.endDate
                                            ? this.state.dateRange.endDate
                                            : date.end,
                                        'MM-dd-y',
                                    )}
                                </>
                            )}
                        </span>
                    </div>
                    {date.items.map((item, sgi) => (
                        <div
                            key={sgi}
                            className={`quickSwitchListItem ${
                                !!this.props.selected &&
                                this.props.selected.initialCriteria ===
                                    item.initialCriteria
                                    ? 'active'
                                    : ''
                            }`}
                            role="button"
                            tabIndex={-1}
                            onClick={() => {
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                this._popUpRef.current &&
                                    this._popUpRef.current.dismiss();
                                this._onSelectionChange(item);
                            }}
                        >
                            <div className="displayName">
                                <div className="d-inline cmps-icon legacy:cmps-cdk-fill-green-100 cmps-cdk-mr-0dot5">
                                    <svg>
                                        {!item.extra.teamSuperGroup && (
                                            <path d={mdiAccount}></path>
                                        )}
                                        {item.extra.teamSuperGroup && (
                                            <path d={mdiAccountMultiple}></path>
                                        )}
                                    </svg>
                                </div>
                                <span>{item.shortName}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ));
        };
        const pivot = (items2: QuickSwitchItem[]): DateRange[] => {
            const piv: DateRange[] = [];
            items2.forEach((e) => {
                e.dateRanges.forEach((ee) => {
                    const key = ee.start.getTime() + ee.end.getTime();
                    const index = piv.findIndex((eee) => eee.key === key);
                    if (index === -1) {
                        piv.push({
                            key,
                            start: ee.start,
                            end: ee.end,
                            items: [e],
                        });
                    } else {
                        piv[index].items.push(e);
                    }
                });
            });
            return piv.sort((a, b) => a.end.getTime() - b.end.getTime());
        };
        const personal = previousSuperGroupColumn(
            pivot(items.filter((e) => !e.extra.teamSuperGroup)),
        );
        const team = previousSuperGroupColumn(
            pivot(items.filter((e) => e.extra.teamSuperGroup)),
        );
        return (
            <div className="col">
                <div className="row mb-2">
                    <div className="col-6">
                        <div className="typeTitle">Personal</div>
                    </div>
                    <div className="col-6">
                        <div className="typeTitle">Subordinates</div>
                    </div>
                </div>
                <div className="row insight-scrollable">
                    <div className="col-6">{personal}</div>
                    <div className="insight-separator" />
                    <div className="col-6">{team}</div>
                </div>
            </div>
        );
    }

    render(): React.ReactNode {
        const superGroupItemList = this._filterRecent();
        const previousSuperGroupList = this._filterPrevious(superGroupItemList);
        const popUpContent = this._getPopUpContent(previousSuperGroupList);
        if (superGroupItemList.length === 0) {
            return null;
        }
        return (
            <div>
                <span className="quickSwitchListTitle">
                    {this.props.quickSwitch.title ||
                        this._translateService.instant(
                            TranslationKeyMap._147_SUPERGROUPS,
                        )}
                </span>
                <div className="d-inline-flex flex-wrap">
                    {superGroupItemList.map((item, i) => (
                        <div
                            key={i}
                            className={`mb-1 quickSwitchListItem ${
                                !!this.props.selected &&
                                this.props.selected.initialCriteria ===
                                    item.initialCriteria
                                    ? 'active'
                                    : ''
                            }`}
                            role="button"
                            tabIndex={-1}
                            onClick={() => this._onSelectionChange(item)}
                        >
                            <div className="displayName">
                                <div className="d-inline cmps-icon legacy:cmps-cdk-fill-green-100 cmps-cdk-mr-0dot5">
                                    <svg>
                                        {!item.extra.teamSuperGroup && (
                                            <path d={mdiAccount}></path>
                                        )}
                                        {item.extra.teamSuperGroup && (
                                            <path d={mdiAccountMultiple}></path>
                                        )}
                                    </svg>
                                </div>
                                <span>{item.shortName}</span>
                            </div>
                            {i === 0 && (
                                <div className="dateRanges">
                                    {item.dateRanges.map((d, k) => (
                                        <span key={k}>
                                            {this._datePipe.transform(
                                                d.start,
                                                'MM-dd-y',
                                            )}
                                            {this.state.dateRange.periodType !==
                                                PeriodType.DAY && (
                                                <>
                                                    {' '}
                                                    -{' '}
                                                    {this._datePipe.transform(
                                                        d.end >
                                                            this.state.dateRange
                                                                .endDate
                                                            ? this.state
                                                                  .dateRange
                                                                  .endDate
                                                            : d.end,
                                                        'MM-dd-y',
                                                    )}
                                                </>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    {previousSuperGroupList.length > 0 && (
                        <>
                            <button
                                className="btn bg-transparent quickSwitchListButton"
                                tabIndex={-1}
                                // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async, @typescript-eslint/prefer-optional-chain
                                onClick={() =>
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    this._popUpRef.current &&
                                    this._popUpRef.current.show()
                                }
                            >
                                <span>
                                    {this.props.quickSwitch.buttonName ||
                                        this._translateService.instant(
                                            TranslationKeyMap._7131_PREVIOUS_SUPERGROUPS,
                                        )}
                                </span>
                                <div className="d-inline cmps-icon cmps-cdk-ml-0dot5">
                                    <svg>
                                        <path d={mdiPlusCircleOutline}></path>
                                    </svg>
                                </div>
                            </button>
                            <PopUp
                                target={() =>
                                    document.querySelector(
                                        '.quickSwitchListButton',
                                    )
                                }
                                placement="right-center"
                                triangle={true}
                                containerClass="insight-super-group insight-pop-up-container"
                                backdrop={true}
                                backdropClass="insight-super-group insight-pop-up-backdrop"
                                backdropDismiss={true}
                                relativeTo={() =>
                                    document.querySelector('[cmpsHumanInsight]')
                                }
                                ref={this._popUpRef}
                            >
                                <PopUpFrameInsight
                                    containerClass="insight-super-group insight-pop-up-frame"
                                    title={this._translateService.instant(
                                        TranslationKeyMap._834_SELECT_A_SUPERGROUP,
                                    )}
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    onCloseClick={() =>
                                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                        this._popUpRef.current &&
                                        this._popUpRef.current.close()
                                    }
                                >
                                    <div className="insight-super-group insight-pop-up-content">
                                        {popUpContent}
                                    </div>
                                </PopUpFrameInsight>
                            </PopUp>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export const SuperGroupHumanInsight = connect<
    {},
    {},
    SuperGroupHumanInsightProps
>((state) => storeStateMapper(state).superGroup)(SuperGroupHumanInsightImpl);

@Component({
    selector: '[rvirus][cmpsHumanInsightSuperGroup]',
    template: ` <cmps-human-insight-super-group
        [cacheDates]="cacheDates"
        [eventDate]="eventDate"
        [selected]="selected"
        (onSelectionChange)="selectionChange.emit($event)"
    ></cmps-human-insight-super-group>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class SuperGroupHumanInsightComponent {
    @Input() cacheDates: CacheDateItem[];
    @Input() eventDate: Date;
    @Input() selected: QuickSwitchItem;
    @Output() selectionChange: EventEmitter<any> = new EventEmitter();
}
